// src/components/SplashScreen.js
import React, { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import logo from "../assets/logo.png";
import startSound from "../assets/startup.mp3"; // <-- YOU MUST ADD THIS FILE

/* ==============================
   ANIMATIONS
============================== */

const fadeOut = keyframes`
  0% { opacity: 1; }
  100% { opacity: 0; visibility: hidden; }
`;

const pulseGlow = keyframes`
  0% { filter: drop-shadow(0 0 6px rgba(41,171,226,0.5)); }
  50% { filter: drop-shadow(0 0 26px rgba(247,148,29,0.9)); }
  100% { filter: drop-shadow(0 0 6px rgba(41,171,226,0.5)); }
`;

const shine = keyframes`
  0% { transform: translateX(-130%); }
  100% { transform: translateX(130%); }
`;

const ripple = keyframes`
  0% { transform: scale(0.7); opacity: 0.5; }
  70% { transform: scale(1.5); opacity: 0.08; }
  100% { transform: scale(2.1); opacity: 0; }
`;

const moveParticles = keyframes`
  from { transform: translateY(0); }
  to { transform: translateY(-160px); }
`;

/* ==============================
   WRAPPER
============================== */

const SplashWrapper = styled.div`
  position: fixed;
  top: 0; left: 0;
  width: 100vw; height: 100vh;

  display: flex;
  justify-content: center;
  align-items: center;

  /* Trend Genie brand palette — sky blue to navy */
  background: radial-gradient(circle at center, #0d3d5c, #071d2e, #030e1a);

  overflow: hidden;
  z-index: 9999;

  animation: ${fadeOut} 0.8s ease forwards;
  animation-delay: 3.2s;
`;

/* ==============================
   PARTICLES
============================== */

const Particles = styled.div`
  position: absolute;
  width: 100%; height: 100%;
  background-image: radial-gradient(rgba(255,255,255,0.1) 2px, transparent 2px);
  background-size: 60px 60px;
  opacity: 0.18;
  animation: ${moveParticles} 15s linear infinite;
`;

/* ==============================
   LOGO
============================== */

const LogoBox = styled.div`
  text-align: center;
  position: relative;
`;

const LogoImg = styled.img`
  width: 100px;
  height: 100px;
  animation: ${pulseGlow} 2.6s infinite ease-in-out;
  position: relative;
  z-index: 2;
`;

const Shine = styled.div`
  position: absolute;
  top: 0; left: 0;
  width: 100px; height: 100px;
  overflow: hidden;
  border-radius: 50%;

  &::before {
    content: "";
    position: absolute;
    top: 0; left: 0;
    width: 45px; height: 100%;
    background: rgba(255,255,255,0.5);
    filter: blur(12px);
    transform: skewX(-20deg);
    animation: ${shine} 1.8s ease-in-out infinite;
  }
`;

const Ripple = styled.div`
  position: absolute;
  top: 50%; left: 50%;
  width: 140px; height: 140px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: radial-gradient(circle, rgba(41,171,226,0.30), rgba(41,171,226,0));
  animation: ${ripple} 2.4s ease-out infinite;
`;

/* ==============================
   PROGRESS BAR
============================== */

const LoaderWrapper = styled.div`
  width: 160px;
  margin: 1.8rem auto 0;
  height: 6px;
  background: rgba(255,255,255,0.12);
  border-radius: 4px;
  overflow: hidden;
`;

const LoaderBar = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #29ABE2, #F7941D);
  width: ${props => props.progress}%;
  transition: width 0.18s ease;
`;

/* ==============================
   LOADING TEXT
============================== */

const LoadingText = styled.p`
  margin-top: 0.7rem;
  color: #cfd2ff;
  font-size: 1.05rem;
  font-weight: 500;
  opacity: 0.85;
`;

/* ==============================
   COMPONENT
============================== */

const SplashScreen = () => {
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);

  // Run progress loader
  useEffect(() => {
    let p = 0;
    const interval = setInterval(() => {
      p += Math.random() * 12; 
      if (p > 100) p = 100;
      setProgress(p);
    }, 180);

    return () => clearInterval(interval);
  }, []);

  // Play startup sound
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = 0.3;
      audioRef.current.play().catch(() => {});
    }
  }, []);

  return (
    <SplashWrapper>
      <Particles />

      <audio ref={audioRef} src={startSound} preload="auto" />

      <LogoBox>
        <Ripple />
        <LogoImg src={logo} alt="Trend Genie" />
        <Shine />

        <LoaderWrapper>
          <LoaderBar progress={progress} />
        </LoaderWrapper>

        <LoadingText>Loading Trend Genie...</LoadingText>
      </LogoBox>
    </SplashWrapper>
  );
};

export default SplashScreen;